// @tier: PREMIUM — Gestion des demandes parents
import { useState, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import unifiedApi from '../services/unifiedApi';
import { PAGINATION } from '../config/constants';

const DEFAULT_STATS = { total: 0, pending: 0, approved: 0, rejected: 0 };
// Référence stable — évite qu'un `?? []` frais à chaque render invalide le
// useMemo de filteredRequests (dépendance qui semblerait changer sans arrêt).
const EMPTY_REQUESTS = [];

export const useRequests = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [nearbyStops, setNearbyStops] = useState([]);
  const [loadingNearbyStops, setLoadingNearbyStops] = useState(false);

  // Id de la demande la plus récemment ouverte dans le détail — permet
  // d'ignorer une réponse nearby-stops tardive si l'admin a rouvert une autre
  // demande entre-temps (évite d'attacher les suggestions de A au détail de B).
  const activeDetailRequestId = useRef(null);

  // Liste paginée côté backend (limit borné à LIMITS.PAGINATION_MAX) — ne pas
  // repasser à un fetch "tout d'un coup" : une école peut dépasser la page
  // courante en nombre de demandes, silencieusement masquer les plus
  // anciennes derrière un total KPI qui, lui, reste exact.
  const {
    data: requestsPage,
    isLoading: loading,
    error: queryError,
    refetch: loadData,
  } = useQuery({
    queryKey: ['requests', filterStatus, page],
    queryFn: async () => {
      const params = { limit: PAGINATION.REQUESTS_LIMIT, page };
      if (filterStatus !== 'all') params.status = filterStatus;
      const r = await unifiedApi.requests.getAll(params);
      if (!r.success) throw new Error(r.message || 'Erreur chargement des demandes');
      return { items: r.data || [], pagination: r.pagination };
    },
  });

  const requests = requestsPage?.items ?? EMPTY_REQUESTS;
  const pagination = requestsPage?.pagination ?? { page: 1, pages: 1, total: 0 };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > (pagination.pages || 1)) return;
    setPage(nextPage);
  };

  const setFilterStatusAndResetPage = (status) => {
    setPage(1);
    setFilterStatus(status);
  };

  // Indépendant du filtre/de la pagination de la liste ci-dessus — sinon les
  // cartes KPI reflètent uniquement le sous-ensemble filtré affiché.
  const { data: stats = DEFAULT_STATS } = useQuery({
    queryKey: ['requests-stats'],
    queryFn: async () => {
      const r = await unifiedApi.requests.getStats();
      if (!r.success) throw new Error(r.message || 'Erreur chargement des statistiques');
      return r.data;
    },
  });

  const error = queryError?.message ?? null;

  const filteredRequests = useMemo(() => {
    if (!searchTerm) return requests;
    const term = searchTerm.toLowerCase();
    return requests.filter((r) => {
      const studentName = `${r.students?.first_name || ''} ${r.students?.last_name || ''}`.toLowerCase();
      const parentName = `${r.parents?.first_name || ''} ${r.parents?.last_name || ''}`.toLowerCase();
      return studentName.includes(term) || parentName.includes(term) || r.id.toLowerCase().includes(term);
    });
  }, [requests, searchTerm]);

  // Les arrêts à proximité sont purement informatifs (voir CLAUDE.md /
  // parentRequestService.approve) — chargés à l'ouverture du détail, jamais
  // dans le flux d'approbation qui n'exige plus aucune sélection.
  const handleViewDetails = async (request) => {
    activeDetailRequestId.current = request.id;
    setSelectedRequest(request);
    setShowDetailDialog(true);
    setNearbyStops([]);

    if (request.type === 'absence') return;

    setLoadingNearbyStops(true);
    try {
      const r = await unifiedApi.requests.getNearbyStops(request.id);
      if (activeDetailRequestId.current !== request.id) return; // demande rouverte entre-temps
      setNearbyStops(r.success ? r.data : []);
    } catch (err) {
      if (activeDetailRequestId.current !== request.id) return;
      toast.error(err.message || 'Erreur lors de la recherche des arrêts à proximité');
      setNearbyStops([]);
    } finally {
      if (activeDetailRequestId.current === request.id) setLoadingNearbyStops(false);
    }
  };

  const closeDetailDialog = () => {
    activeDetailRequestId.current = null;
    setShowDetailDialog(false);
    setSelectedRequest(null);
    setNearbyStops([]);
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setActionType('approve');
    setActionNote('');
    setShowActionDialog(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setActionType('reject');
    setActionNote('');
    setShowActionDialog(true);
  };

  const closeActionDialog = () => {
    setShowActionDialog(false);
    setSelectedRequest(null);
    setActionNote('');
  };

  const confirmAction = async () => {
    if (!selectedRequest) return;

    try {
      if (actionType === 'approve') {
        const r = await unifiedApi.requests.approve(selectedRequest.id, {
          note: actionNote || undefined,
        });
        if (!r.success) {
          toast.error(r.message || "Erreur lors de l'approbation");
          return;
        }
        toast.success('Demande approuvée — le parent a été notifié');
      } else {
        if (!actionNote.trim()) {
          toast.error('Merci d’indiquer un motif de refus');
          return;
        }
        const r = await unifiedApi.requests.reject(selectedRequest.id, { reason: actionNote });
        if (!r.success) {
          toast.error(r.message || 'Erreur lors du refus');
          return;
        }
        toast.success('Demande refusée — le parent a été notifié');
      }
    } catch (err) {
      toast.error(err.message || 'Erreur de connexion — réessayez');
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['requests'] });
    queryClient.invalidateQueries({ queryKey: ['requests-stats'] });
    closeActionDialog();
  };

  return {
    requests,
    filteredRequests,
    stats,
    loading,
    error,
    loadData,
    searchTerm,
    setSearchTerm,
    filterStatus,
    setFilterStatus: setFilterStatusAndResetPage,
    pagination,
    goToPage,
    selectedRequest,
    showDetailDialog,
    // Pas un setState brut : sur fermeture (open=false), réinitialise aussi
    // selectedRequest/nearbyStops/activeDetailRequestId — d'où le nom
    // distinct de "setShowDetailDialog", pour que l'appelant ne le confonde
    // pas avec le setter d'état React.
    onDetailDialogOpenChange: (open) => (open ? setShowDetailDialog(true) : closeDetailDialog()),
    showActionDialog,
    // Pas un setState brut : sur fermeture (open=false), réinitialise aussi
    // selectedRequest/actionNote — d'où le nom distinct de
    // "setShowActionDialog", pour que l'appelant ne le confonde pas avec le
    // setter d'état React.
    onActionDialogOpenChange: (open) => (open ? setShowActionDialog(true) : closeActionDialog()),
    actionType,
    actionNote,
    setActionNote,
    nearbyStops,
    loadingNearbyStops,
    handleViewDetails,
    handleApprove,
    handleReject,
    confirmAction,
  };
};

export default useRequests;
